import { PalletizerStatus } from 'libapi';
import { useContext, useState, useEffect, useMemo } from 'react';
import {
	PalletizerContext,
	RuntimeContext,
} from '../../context/PalletizerContext';
import { Loading } from '../Layout';
import Button from '../PendantButton';
import { COLOR } from '../shared/shared';

interface RobotControlButtonProps {
	pIconClass: string;
	pText: string;
	pIsLoading: boolean;
	pDisabled: boolean;
	pAction: () => void;
	pColor?: COLOR;
}

export function RobotControlButton({
	pIconClass,
	pText,
	pIsLoading,
	pDisabled,
	pAction,
	pColor,
}: RobotControlButtonProps) {
	return (
		<Button
			pClass={'height-auto padding vw-11'}
			pAction={pAction}
			pDisabled={pDisabled}
			pNormalColorOverride={pColor ? pColor : COLOR.TRANSPARENT}
			pClickRed={pText === 'STOP'}
		>
			<div className="flex-row align-center">
				<div className="flex-col align-center text-align-center">
					<span className={`font-6 full-width ${pIconClass}`} />
					{pText.length > 0 && (
						<span
							className={
								'font-5 bold-font ' + (pIconClass.length > 0 ? 'margin-top' : '')
							}
						>
							{pText}
						</span>
					)}
				</div>
				{pIsLoading && <Loading className="white-loading-spinner" />}
			</div>
		</Button>
	);
}

interface RobotControlMenuProps {
	pHandleRunInfeed: () => void;
	pHandleHome: () => void;
	pHandleStart: () => void;
	pHandleStop: () => void;
	pStartEnabled: boolean;
	webDeploymentOverride: boolean;
}

export function RobotControlMenu({
	pHandleHome,
	pHandleStart,
	pHandleStop,
	pStartEnabled,
	webDeploymentOverride,
}: RobotControlMenuProps) {
	const lRuntimeContext = useContext(PalletizerContext) as RuntimeContext;
	const status = lRuntimeContext.operational_state.status;
	const [lStopping, lSetStopping] = useState<boolean>(false);

	// Debounce flags
	const [lDisableHome, lSetDisableHome] = useState<boolean>(false);
	const [lDisableStart, lSetDisableStart] = useState<boolean>(false);
	const [lDisableStop, lSetDisableStop] = useState<boolean>(false);

	useEffect(() => {
		if (lStopping && status !== PalletizerStatus.Running) {
			lSetStopping(false);
		}
	}, [status]);

	const isDisabled = useMemo(() => {
		return (
			lDisableStop ||
			lStopping ||
			[
				PalletizerStatus.Fault,
				PalletizerStatus.Offline,
				PalletizerStatus.Ready,
			].includes(status)
		);
	}, [lDisableStop, lStopping, status]);

	return (
		<div
			className="flex-row flex-center double-margin-bottom double-margin-top "
			style={{ zIndex: '4' }}
		>
			{/* <RobotControlButton
				pIconClass={'icon-belt-actuator'}
				pText={'MANUAL'}
				pDisabled={status !== PalletizerStatus.Ready}
				pIsLoading={false}
				pAction={() => {
					pHandleRunInfeed();
					lSetDisableStart(true);
				}}
				pColor={COLOR.MEDIUM_BLUE}
			/> */}
			<div className="padding"></div>
			<RobotControlButton
				pIconClass={'icon-edit-robot'}
				pText={'HOME'}
				pDisabled={lDisableHome || status !== PalletizerStatus.Ready}
				pIsLoading={false}
				pAction={() => {
					pHandleHome();
					lSetDisableHome(true);
					setTimeout(() => {
						lSetDisableHome(false);
					}, 1000);
				}}
				pColor={
					lDisableStart || status !== PalletizerStatus.Ready
						? COLOR.DARK_GREY
						: COLOR.MEDIUM_BLUE
				}
			/>
			<div className="padding"></div>
			<RobotControlButton
				pIconClass={'icon-play'}
				pText={'PLAY'}
				pDisabled={
					!webDeploymentOverride &&
					(lDisableStart || !pStartEnabled || status !== PalletizerStatus.Ready)
				}
				pIsLoading={false}
				pAction={() => {
					pHandleStart();
					lSetDisableStart(true);
					setTimeout(() => {
						lSetDisableStart(false);
					}, 1000);
				}}
				pColor={
					(lDisableStart || !pStartEnabled || status !== PalletizerStatus.Ready) &&
					!webDeploymentOverride
						? COLOR.DARK_GREY
						: COLOR.GREEN
				}
			/>
			<div className="padding"></div>
			<RobotControlButton
				pIconClass={'icon-stop'}
				pText={lStopping ? 'STOPPING...' : 'STOP'}
				pDisabled={isDisabled}
				pIsLoading={false}
				pAction={() => {
					if (status === PalletizerStatus.Running) {
						lSetStopping(true);
						// Failsafe. We could add a stopping state. But I don't know that its worth it
						setTimeout(() => {
							lSetStopping(false);
						}, 30000);
					}
					pHandleStop();
					lSetDisableStop(true);
					lSetDisableStart(false);
					setTimeout(() => {
						lSetDisableStop(false);
					}, 1000);
				}}
				pColor={isDisabled ? COLOR.DARK_GREY : COLOR.RED_CLEAR}
			/>
		</div>
	);
}
